import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, FormGroup, Input, Label, Modal, ModalBody, ModalHeader } from 'reactstrap';
import { skipToken } from '@reduxjs/toolkit/query';
import { saveReportingLevelDetails } from '../../../../actions/initiative';
import { useAppDispatch } from '@reducers/index';
import { useGetSubsidiaryGroupsQuery, useUpdateSubsidiaryGroupsMutation } from '@api/subsidiary-groups';
import { useAddSubsidiaryMutation } from '@api/initiatives';
import { MultipleSelect } from '@g17eco/molecules/select/MultipleSelect';
import { generateToast, toastFromError } from '@g17eco/molecules/toasts';
import { generateUrl } from '@routes/util';
import { ROUTES } from '@constants/routes';

interface SubsidiaryModalProps {
  isOpen: boolean;
  isEdit: boolean;
  initiativeSelected?: { initiativeId: string; name?: string };
  rootInitiativeId?: string;
  canManageSubsidiaryGroups?: boolean;
  toggle: () => void;
  handleUpdate: () => void;
}

export const SubsidiaryModal = (props: SubsidiaryModalProps) => {
  const { toggle, isOpen, initiativeSelected, isEdit, handleUpdate, rootInitiativeId, canManageSubsidiaryGroups } = props;
  const dispatch = useAppDispatch();
  const history = useHistory();

  const { data: subsidiaryGroups = [] } = useGetSubsidiaryGroupsQuery(rootInitiativeId ?? skipToken);
  const [updateSubsidiaryGroups] = useUpdateSubsidiaryGroupsMutation();
  const [addSubsidiary] = useAddSubsidiaryMutation();

  const currentGroupIds = useMemo(
    () =>
      subsidiaryGroups
        .filter((group) => group.group.some((member) => member.initiativeId === initiativeSelected?.initiativeId))
        .map((g) => g._id),
    [subsidiaryGroups, initiativeSelected?.initiativeId]
  );

  const groupOptions = useMemo(
    () => subsidiaryGroups.map((group) => ({ value: group._id, label: group.name, searchString: group.name })),
    [subsidiaryGroups]
  );

  const [initiativeName, setInitiativeName] = useState(() => (isEdit ? initiativeSelected?.name ?? '' : ''));
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);

  useEffect(() => {
    if (isEdit && currentGroupIds.length > 0) {
      setSelectedGroupIds(currentGroupIds);
    }
  }, [isEdit, currentGroupIds]);

  if (!initiativeSelected) {
    return null;
  }

  const handleManageGroups = () => {
    if (!rootInitiativeId) {
      return;
    }
    toggle();
    history.push(generateUrl(ROUTES.MANAGE_SUBSIDIARY_GROUPS, { initiativeId: rootInitiativeId }));
  };

  const getGroupsDiff = (currentIds: string[] = currentGroupIds) => {
    const groupIdsToAdd = selectedGroupIds.filter((id) => !currentIds.includes(id));
    const groupIdsToRemove = currentIds.filter((id) => !selectedGroupIds.includes(id));
    return { groupIdsToAdd, groupIdsToRemove };
  };

  const hasGroupsChanged = () => {
    const { groupIdsToAdd, groupIdsToRemove } = getGroupsDiff();
    return groupIdsToAdd.length > 0 || groupIdsToRemove.length > 0;
  };

  const assignSubsidiaryToGroups = async (subsidiaryId: string, currentIds: string[] = currentGroupIds) => {
    if (!rootInitiativeId) {
      return;
    }

    const { groupIdsToAdd, groupIdsToRemove } = getGroupsDiff(currentIds);
    if (groupIdsToAdd.length === 0 && groupIdsToRemove.length === 0) {
      return;
    }

    await updateSubsidiaryGroups({
      rootInitiativeId,
      subsidiaryId,
      groupIdsToAdd,
      groupIdsToRemove,
    }).unwrap();
  };

  const handleEdit = async () => {
    if (!initiativeName) {
      return;
    }

    try {
      await dispatch(saveReportingLevelDetails(initiativeSelected.initiativeId, { name: initiativeName }));
      if (hasGroupsChanged()) {
        await assignSubsidiaryToGroups(initiativeSelected.initiativeId);
      }

      generateToast({
        color: 'success',
        title: 'Success',
        message: 'Subsidiary updated successfully',
      });
      toggle();
      handleUpdate();
    } catch (error) {
      toastFromError({ error, title: 'Failed to update subsidiary' });
    }
  };

  const handleAdd = async () => {
    if (!initiativeName) {
      return;
    }

    try {
      const created = await addSubsidiary({
        parentInitiativeId: initiativeSelected.initiativeId,
        name: initiativeName,
      }).unwrap();

      await assignSubsidiaryToGroups(created._id, []);

      generateToast({
        color: 'success',
        title: 'Success',
        message: 'Subsidiary added successfully',
      });
      toggle();
      handleUpdate();
    } catch (error) {
      toastFromError({ error, title: 'Failed to add subsidiary' });
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} backdrop='static' className='initiative-modal'>
      <ModalHeader toggle={toggle}>{isEdit ? 'Update subsidiary' : 'Add subsidiary'}</ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label for='subsidiaryName'>
            <h6 className='my-0 text-ThemeHeadingDark'>Subsidiary name</h6>
          </Label>
          <Input
            placeholder='Enter name'
            onChange={(e) => setInitiativeName(e.target.value)}
            value={initiativeName}
          />
        </FormGroup>

        {rootInitiativeId && (
          <FormGroup>
            <div className='d-flex justify-content-between align-items-center mb-2'>
              <Label for='assignSubsidiaryToGroup' className='mb-0'  >
                <h6 className='my-0 text-ThemeHeadingDark'>Assign subsidiary to group (optional)</h6>
              </Label>
              {canManageSubsidiaryGroups && (
                <Button color='link' className='p-0 text-primary' onClick={handleManageGroups}>
                  Manage subsidiary groups
                </Button>
              )}
            </div>
            <MultipleSelect
              placeholder={`Select groups (${selectedGroupIds.length})`}
              options={groupOptions}
              values={selectedGroupIds}
              onChange={setSelectedGroupIds}
            />
          </FormGroup>
        )}

        <div className='mt-3 text-right'>
          <Button className='mr-2' color='link-secondary' onClick={toggle}>
            Cancel
          </Button>
          <Button
            className='btn btn-sm'
            color='primary'
            onClick={isEdit ? handleEdit : handleAdd}
            disabled={!initiativeName}
          >
            {isEdit ? 'Update' : 'Add'}
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );
};
